import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", denied: "bg-slate-200 text-slate-700" };

const RefundForm = ({ id, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/marketplace/purchases/${id}/refund-request`, { reason }); toast.success("Refund requested - your administrator will review it"); onDone(); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2">
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you requesting a refund? (e.g. wrong number, not a real lead)" data-testid={`refund-reason-${id}`} />
      <Button size="sm" disabled={busy || reason.trim().length < 5} onClick={send} data-testid={`refund-submit-${id}`}>Send refund request</Button>
    </div>
  );
};

const Row = ({ p, open, setOpen, reload }) => (
  <div className="py-3" data-testid={`purchase-${p.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><b>{p.name || "Marketplace lead"}</b> <span className="text-sm text-slate-500">{[p.loan_purpose, p.location].filter(Boolean).join(" · ")}</span>
        <div className="text-xs text-slate-400">${p.price_paid.toFixed(2)} · bought {new Date(p.sold_at).toLocaleDateString()}</div></div>
      {p.refund_status ? <Badge className={`border-0 capitalize ${TONE[p.refund_status]}`} data-testid={`refund-status-${p.id}`}>Refund {p.refund_status}</Badge> : null}
      {p.can_request && open !== p.id && <Button size="sm" variant="outline" onClick={() => setOpen(p.id)} data-testid={`refund-request-btn-${p.id}`}>Request refund</Button>}
    </div>
    {open === p.id && <RefundForm id={p.id} onDone={() => { setOpen(""); reload(); }} />}
  </div>
);

export const MyPurchases = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const load = useCallback(() => api.get("/marketplace/purchases").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data || data.purchases.length === 0) return null;
  return (
    <Card className="mt-6 p-5" data-testid="my-purchases">
      <h3 className="font-semibold text-slate-900">My recent purchases</h3>
      <p className="text-sm text-slate-500">You can request a refund within {data.refund_days} days of buying a paid lead. Your administrator reviews each request and sends approved refunds to you directly; the lead is then removed from your leads.</p>
      <div className="divide-y divide-slate-100">{data.purchases.map((p) => <Row key={p.id} p={p} open={open} setOpen={setOpen} reload={load} />)}</div>
    </Card>
  );
};
